// src/contexts/FeaturesContext.tsx

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

export interface UserFeatures {
    questions: boolean;
    stats: boolean;
    statsFlashcardsOnly: boolean; // ✅ NOVO: stats liberado só para sub-aba flashcards
    planner: boolean;
    groups: boolean;
    isEditor: boolean;
    isLegacy: boolean;
}

interface FeaturesContextType {
    features: UserFeatures;
    loading: boolean;
    isEditor: boolean;
    canAccess: (feature: keyof Omit<UserFeatures, 'isEditor' | 'isLegacy' | 'statsFlashcardsOnly'>) => boolean;
    refreshFeatures: () => Promise<void>;
}

const defaultFeatures: UserFeatures = {
    questions: false,
    stats: false,
    statsFlashcardsOnly: false,
    planner: false,
    groups: false,
    isEditor: false,
    isLegacy: false,
};

const FeaturesContext = createContext<FeaturesContextType>({
    features: defaultFeatures,
    loading: true,
    isEditor: false,
    canAccess: () => false,
    refreshFeatures: async () => { },
});

export const FeaturesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, isAdmin } = useAuth();
    const [features, setFeatures] = useState<UserFeatures>(defaultFeatures);
    const [loading, setLoading] = useState(true);

    const fetchFeatures = useCallback(async () => {
        if (isAdmin) {
            setFeatures({
                questions: true,
                stats: true,
                statsFlashcardsOnly: false,
                planner: true,
                groups: true,
                isEditor: true,
                isLegacy: false,
            });
            setLoading(false);
            return;
        }

        if (!user) {
            setFeatures(defaultFeatures);
            setLoading(false);
            return;
        }

        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`/api/dashboard/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!res.ok) throw new Error('Falha ao buscar features');

            const data = await res.json();
            setFeatures({
                ...defaultFeatures,
                ...(data.features || {}),
                questions: true,
                stats: true,
            });
        } catch (err) {
            console.error('❌ [FeaturesContext] Erro ao buscar features:', err);
            setFeatures(defaultFeatures);
        } finally {
            setLoading(false);
        }
    }, [user, isAdmin]);

    useEffect(() => {
        fetchFeatures();
    }, [fetchFeatures]);

    const canAccess = useCallback(
        (feature: keyof Omit<UserFeatures, 'isEditor' | 'isLegacy' | 'statsFlashcardsOnly'>): boolean => {
            if (isAdmin) return true;
            return features[feature] === true;
        },
        [features, isAdmin]
    );

    return (
        <FeaturesContext.Provider value={{
            features,
            loading,
            isEditor: features.isEditor,
            canAccess,
            refreshFeatures: fetchFeatures
        }}>
            {children}
        </FeaturesContext.Provider>
    );
};

export const useFeatures = () => useContext(FeaturesContext);

export default FeaturesContext;